import { orgAPI } from "@/api/api";
import { fileRecordKeys } from "@/api/fileRecords";
import type { FileRecord } from "@cohesive/api-client";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Return every record nested under a folder (files and sub-folders), excluding
 * the folder itself. A descendant is any record whose path starts with the
 * folder's path. Folder paths end with `/` so a folder `foo/` won't match a
 * sibling file `foo-bar`.
 */
export function getFolderDescendants(
  folder: FileRecord,
  allRecords: FileRecord[],
): FileRecord[] {
  if (!folder.path.endsWith("/")) return [];
  return allRecords.filter(
    (r) => r.id !== folder.id && r.path.startsWith(folder.path),
  );
}

function fileRecordListKey(
  record: FileRecord,
  workspaceId?: string,
): readonly unknown[] | null {
  if (record.class === "workspace") {
    const scopedWorkspaceId = record.workspace_id ?? workspaceId;
    return scopedWorkspaceId
      ? fileRecordKeys.workspace(scopedWorkspaceId)
      : null;
  }
  if (record.class === "canvas" && record.canvas_id) {
    return fileRecordKeys.canvas(record.canvas_id);
  }
  if (record.class === "flow-session" && record.flow_session_id) {
    return fileRecordKeys.flowSession(record.flow_session_id);
  }
  return null;
}

function recentlyDeletedKey(
  record: FileRecord,
  workspaceId?: string,
): readonly unknown[] | null {
  if (record.class === "workspace") {
    const scopedWorkspaceId = record.workspace_id ?? workspaceId;
    return scopedWorkspaceId
      ? fileRecordKeys.recentlyDeleted("workspace", scopedWorkspaceId)
      : null;
  }
  if (record.class === "canvas" && record.canvas_id) {
    return fileRecordKeys.recentlyDeleted("canvas", record.canvas_id);
  }
  return null;
}

/**
 * Delete a folder along with everything inside it.
 *
 * The backend's `DELETE /file-records/:id` only touches the targeted
 * row, so we explicitly remove every descendant first. This used to
 * fan the deletes out through `useDeleteFileRecord.mutateAsync`, which
 * had two failure modes for populated folders:
 *
 * 1. Each per-record mutation's `onSettled` invalidates the scoped
 *    list cache. Multiple invalidations racing in parallel against
 *    parallel `DELETE` calls meant a refetch could land while some
 *    descendants were still queued server-side, briefly resurrecting
 *    them in the cache and surfacing them to the UI.
 * 2. `mutateAsync`'s optimistic remove only takes one record at a time.
 *    Between the folder marker disappearing and a refetch returning,
 *    nothing was hiding the children — they could flash at their
 *    original nested paths in any view that listed them flatly.
 *
 * The new flow batches the optimistic removal up front (folder marker
 * + every descendant in a single cache update), bypasses the mutation
 * hook to avoid per-call invalidations, and runs a single backstop
 * invalidation only after every `DELETE` settled. On any per-record
 * failure, the refetch restores whatever survived server-side before
 * the error is surfaced to the caller.
 */
export async function cascadeDeleteFolder(
  folder: FileRecord,
  allRecords: FileRecord[],
  queryClient: QueryClient,
  workspaceId?: string,
): Promise<void> {
  const descendants = getFolderDescendants(folder, allRecords);
  const allIds = new Set<string>([folder.id, ...descendants.map((r) => r.id)]);
  const listKey = fileRecordListKey(folder, workspaceId);
  const deletedKey = recentlyDeletedKey(folder, workspaceId);

  // Cancel any in-flight refetches so they don't race the optimistic
  // batch removal.
  if (listKey) {
    await queryClient.cancelQueries({ queryKey: listKey });

    queryClient.setQueryData<FileRecord[]>(listKey, (prev) =>
      prev ? prev.filter((r) => !allIds.has(r.id)) : prev,
    );
  }
  // Drop per-id detail caches too — anything subscribed via
  // `useFileRecord` / `useFileRecordsByIds` should stop showing the
  // record immediately rather than waiting for a 404 to propagate.
  for (const id of allIds) {
    queryClient.removeQueries({ queryKey: fileRecordKeys.detail(id) });
  }

  let cascadeError: unknown = undefined;
  try {
    const settled = await Promise.allSettled(
      descendants.map((r) => orgAPI.fileRecords.delete(r.id)),
    );
    if (settled.some((s) => s.status === "rejected")) {
      cascadeError = new Error(
        "folder cascade: some descendants failed to delete",
      );
    } else {
      // Folder marker last so a partial failure leaves the folder
      // visible at its original location with whatever children survived.
      await orgAPI.fileRecords.delete(folder.id);
    }
  } catch (err) {
    cascadeError = err;
  }

  // Single backstop invalidation, regardless of outcome. By the time
  // we get here every API call has either resolved or rejected, so
  // there's no race against in-flight deletes — a refetch returns the
  // authoritative server state. On a partial failure this re-adds the
  // descendants/folder that survived (rather than naively rolling
  // back to a pre-removal snapshot, which would resurrect rows we
  // successfully deleted server-side and leave the cache lying about
  // state). The SSE handler typically settles the cache via
  // `file_record.deleted` events on its own; this guarantees eventual
  // consistency in the (rare) case an event was missed.
  if (listKey) {
    queryClient.invalidateQueries({ queryKey: listKey });
  }
  if (deletedKey) {
    queryClient.invalidateQueries({ queryKey: deletedKey });
  }

  if (cascadeError) throw cascadeError;
}
